import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyAvatarModule } from '@vendasta/galaxy/avatar';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { GalaxyPopoverModule } from '@vendasta/galaxy/popover';
import { OverlayModule } from '@angular/cdk/overlay';
import { ConversationDetail } from '../../../../core/src/lib/interface/conversation.interface';
import { AssignableUser, ConversationAssignService } from './inbox-conversation-assign-picker.service';

@Component({
  selector: 'inbox-conversation-assign-picker',
  templateUrl: './inbox-conversation-assign-picker.component.html',
  styleUrls: ['./inbox-conversation-assign-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ConversationAssignService],
  imports: [
    AsyncPipe,
    TranslateModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatInputModule,
    OverlayModule,
    GalaxyAvatarModule,
    GalaxyBadgeModule,
    GalaxyFormFieldModule,
    GalaxyLoadingSpinnerModule,
    GalaxyPopoverModule,
  ],
})
export class InboxConversationAssignPickerComponent {
  readonly assignService = inject(ConversationAssignService);

  readonly selectedUser = this.assignService.selectedUser;
  readonly assigning = this.assignService.assigning;
  readonly resolvingName = this.assignService.resolvingName;
  readonly $assigneeState = this.assignService.$assigneeState;
  readonly $badgeColor = this.assignService.$badgeColor;
  readonly users$ = this.assignService.users$;

  readonly searchControl = new FormControl('', { nonNullable: true });

  displayUser = (user: AssignableUser | string | null): string => {
    if (!user) return '';
    if (typeof user === 'string') return user;
    return user.displayName;
  };

  @Input() set conversationDetail(details: ConversationDetail | null) {
    this.searchControl.setValue('', { emitEvent: false });
    this.assignService.setConversationDetail(details);
  }

  constructor() {
    this.searchControl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((term) => this.assignService.setSearchTerm(typeof term === 'string' ? term : ''));
  }

  onUserSelected(event: MatAutocompleteSelectedEvent): void {
    if (this.assigning()) return;
    this.assignService.assignUser(event.option.value as AssignableUser | null);
  }

  assignToMe(): void {
    this.assignService.assignToMe();
  }

  assignUser(user: AssignableUser | null): void {
    this.assignService.assignUser(user);
  }
}
